import React, { useMemo } from 'react';
import { Box, Paper, Stack, Typography, Button, Divider } from '@mui/material';
import { isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import { getDateFormat, getTimeFormat } from '@utils/dateHelper';
import type { Reservation } from '@app-types/models/reservation';

interface RailCardProps {
  accent: string;
  name?: string;
  timeText?: string;
  subText?: React.ReactNode;
  children?: React.ReactNode;
}

// A single actionable card in the rail.
const RailCard: React.FC<RailCardProps> = ({ accent, name, timeText, subText, children }) => (
  <Box
    sx={{
      border: '1px solid', borderColor: 'divider',
      borderLeft: '4px solid', borderLeftColor: accent,
      borderRadius: 2, p: 1.5, mx: 1.5, mb: 1.25,
      transition: 'box-shadow .15s, transform .15s',
      '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
      <Typography fontWeight={700} fontSize={14} noWrap>{name}</Typography>
      <Typography fontSize={12} color="text.secondary" whiteSpace="nowrap">{timeText}</Typography>
    </Stack>
    <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.25, mb: 1.25 }}>{subText}</Typography>
    <Stack direction="row" spacing={1}>{children}</Stack>
  </Box>
);

const planName = (reservation: Reservation, lang: string): string =>
  (reservation?.planId?.[lang === 'zh' ? 'zh_name' : 'name'] as string) || reservation?.planId?.name || '';

interface ActionRailProps {
  reservations: Reservation[];
  stylistId?: string;
  onApprove: (r: Reservation) => void;
  onComplete: (r: Reservation) => void;
  onNoShow: (r: Reservation) => void;
  onMessage: (r: Reservation) => void;
  onViewAll: () => void;
}

// Left "Needs attention" rail: pending approvals + past sessions to confirm.
const ActionRail: React.FC<ActionRailProps> = ({ reservations, stylistId, onApprove, onComplete, onNoShow, onMessage, onViewAll }) => {
  const { t, i18n } = useTranslation();

  const matchesStylist = (r: Reservation): boolean => !stylistId || r.staffId?._id === stylistId;

  const pending = useMemo(
    () => reservations.filter((r) => r.status === 'pending-for-approval' && matchesStylist(r)),
    [reservations, stylistId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toConfirm = useMemo(
    () => reservations.filter((r) => isPast(new Date(r.start)) && r.status === 'confirmed' && matchesStylist(r)),
    [reservations, stylistId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const total = pending.length + toConfirm.length;

  return (
    <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontSize={15} fontWeight={700}>{t('reservationsV2.needsAttention')}</Typography>
        <Box sx={{ bgcolor: total ? 'primary.main' : 'text.disabled', color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 999, px: 1, py: 0.2, minWidth: 24, textAlign: 'center' }}>
          {total}
        </Box>
      </Stack>

      {total === 0 && (
        <Typography color="text.secondary" fontSize={13} sx={{ textAlign: 'center', py: 4, px: 2 }}>
          {t('reservationsV2.noAttentionItems')}
        </Typography>
      )}

      {pending.length > 0 && (
        <>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: 'text.secondary' }}>
            {t('reservationsV2.pendingApproval')} · {pending.length}
          </Typography>
          {pending.map((r) => (
            <RailCard
              key={r._id}
              accent="#B7791F"
              name={r.customerName}
              timeText={`${getDateFormat(r.start as unknown as Date)} · ${getTimeFormat(r.start)}`}
              subText={`${planName(r, i18n.language)}${r.discountedAmount != null ? ` · $${r.discountedAmount}` : ''}`}
            >
              <Button size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />} onClick={() => onApprove(r)}>
                {t('reservationsV2.approve')}
              </Button>
              <Button size="small" variant="outlined" color="secondary" startIcon={<MessageOutlinedIcon />} onClick={() => onMessage(r)}>
                {t('reservationsV2.message')}
              </Button>
            </RailCard>
          ))}
        </>
      )}

      {toConfirm.length > 0 && (
        <>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: 'text.secondary' }}>
            {t('reservationsV2.confirmPastSessions')} · {toConfirm.length}
          </Typography>
          {toConfirm.map((r) => (
            <RailCard
              key={r._id}
              accent="#2C5D8A"
              name={r.customerName}
              timeText={`${getDateFormat(r.start as unknown as Date)} · ${getTimeFormat(r.start)}`}
              subText={`${planName(r, i18n.language)} — ${t('reservationsV2.didItHappen')}`}
            >
              <Button size="small" variant="contained" color="success" onClick={() => onComplete(r)}>
                {t('reservationsV2.done')}
              </Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => onNoShow(r)}>
                {t('reservationsV2.noShow')}
              </Button>
            </RailCard>
          ))}
        </>
      )}

      <Divider sx={{ mt: 1 }} />
      <Box sx={{ p: 2 }}>
        <Button fullWidth variant="outlined" color="secondary" onClick={onViewAll}>
          {t('reservationsV2.viewAllBookings')}
        </Button>
      </Box>
    </Paper>
  );
};

export default ActionRail;
